import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

export interface MapProvider {
  initializeMap(element: HTMLElement, options: any): Promise<any>;
  createMarker(position: any, options?: any): any;
  removeMarker(marker: any): void;
  clearMarkers(markers: any[]): void;
  clearAllMarkers(): void;
  removeMarkerFromList(marker: any): void;
  getMarkers(): any[];
  addMarker(marker: any): void;
  getMap(): any;
  calculateRoute(request: any): Promise<any>;
  updateMarkerPosition(marker: any, position: any): void;
  setMapCenter(map: any, center: any): void;
  destroyMap(map: any): void;
}

export interface MarkerOptions {
  position?: any;
  icon?: any;
  title?: string;
  clickable?: boolean;
  draggable?: boolean;
  iconSize?: { width: number; height: number };
  iconAnchor?: { x: number; y: number };
  zIndex?: number;
}

export interface RouteRequest {
  origin: any;
  destination: any;
  waypoints?: any[];
  travelMode?: string;
  // Paramètres standardisés pour la cohérence
  avoidHighways?: boolean;
  avoidTolls?: boolean;
  avoidFerries?: boolean;
  optimizeWaypoints?: boolean;
  unit?: 'metric' | 'imperial';
}

export interface RouteResult {
  distance: { Km: number; M: number };
  duration: { hours: number; minutes: number };
  route: any;
}

// Constantes pour standardiser les paramètres de routage
export const ROUTING_CONSTANTS = {
  TRAVEL_MODES: {
    DRIVING: 'DRIVING',
    WALKING: 'WALKING',
    BICYCLING: 'BICYCLING',
    TRANSIT: 'TRANSIT'
  },
  UNITS: {
    METRIC: 'metric',
    IMPERIAL: 'imperial'
  },
  DEFAULT_OPTIONS: {
    avoidHighways: false,
    avoidTolls: false,
    avoidFerries: false,
    optimizeWaypoints: false,
    unit: 'metric'
  }
};

@Injectable({
  providedIn: 'root'
})
export abstract class BaseMapProvider implements MapProvider {
  protected map: any = null;
  protected markers: any[] = [];

  abstract initializeMap(element: HTMLElement, options: any): Promise<any>;
  abstract createMarker(position: any, options?: any): any;
  abstract removeMarker(marker: any): void;
  abstract clearMarkers(markers: any[]): void;
  abstract calculateRoute(request: any): Promise<any>;
  abstract updateMarkerPosition(marker: any, position: any): void;
  abstract setMapCenter(map: any, center: any): void;
  abstract destroyMap(map: any): void;

  getMap(): any {
    return this.map;
  }

  getMarkers(): any[] {
    return this.markers;
  }

  addMarker(marker: any): void {
    this.markers.push(marker);
  }

  removeMarkerFromList(marker: any): void {
    const index = this.markers.indexOf(marker);
    if (index > -1) {
      this.markers.splice(index, 1);
    }
  }

  clearAllMarkers(): void {
    this.markers.forEach(marker => this.removeMarker(marker));
    this.markers = [];
  }

  /**
   * Méthode utilitaire pour normaliser les coordonnées
   */
  protected normalizeCoordinates(position: any): { lat: number; lng: number } {
    return {
      lat: position.lat || position.latitude,
      lng: position.lng || position.longitude
    };
  }

  /**
   * Méthode utilitaire pour valider une requête de route
   */
  protected validateRouteRequest(request: RouteRequest): boolean {
    if (!request.origin || !request.destination) {
      console.error('Route request missing origin or destination');
      return false;
    }
    
    const origin = this.normalizeCoordinates(request.origin);
    const destination = this.normalizeCoordinates(request.destination);
    
    if (!origin.lat || !origin.lng || !destination.lat || !destination.lng) {
      console.error('Invalid coordinates in route request');
      return false;
    }
    
    return true;
  }
} 